import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import CountUp from "react-countup";
import { Menu } from "react-feather";
import dayjs from "dayjs";
import { getUserById } from "../../service/userService";
import { notification } from "antd";
import ProPic from "../../assets/users/PIC.png";
import {
  getThisWeekBirthdays,
  getTodayLeaves,
} from "../../service/dashboardSerice";
import axios from "axios";
import { base_path_img } from "../../environment.jsx";

const EmployeeDashboard = () => {
  const [rightPanelVisible, setRightPanelVisible] = useState(
    window.innerWidth > 768
  );
  const [todayAttendance] = useState(null);
  const [isClockInDisabled, setIsClockInDisabled] = useState(false);
  const [isClockOutDisabled, setIsClockOutDisabled] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [daysInMonth, setDaysInMonth] = useState(0);
  const [firstDayOfMonth, setFirstDayOfMonth] = useState(0);
  const [currentTime, setCurrentTime] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [, setLoading] = useState(false);
  const [thisWeekBirthdays, setThisWeekBirthdays] = useState([]);
  const [, setTodayLeaves] = useState([]);
  // Stable: read localStorage once on mount into state, not on every render
  const [userData] = useState(() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const profileImageUrl = ProPic;
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const [userDetail, setUserDetail] = useState(null);
  // Guard: ensure fetchTodayData runs only once even if effects re-run
  const hasFetchedToday = useRef(false);

  const [api, contextHolder] = notification.useNotification();
  const [monthlySummary, setMonthlySummary] = useState({
    attendance: {
      totalPresentDays: 0,
      totalWorkingDays: 0,
      totalAbsentDays: 0,
    },
    leaves: {
      totalLeaveDays: 0,
    },
    leaveBalances: [],
  });
  const [leaveDetails, setLeaveDetails] = useState([]);
  const [countLate] = useState(0); // Assuming late count is not provided in new APIs

  const userObj = userData?.data;

  const showNotification = (type, message, description) => {
    api[type]({
      message,
      description,
      placement: 'topRight',
      duration: 4.5,
    });
  };

  const updateCalendar = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    setSelectedMonth(`${monthNames[month]} ${year}`);
    setDaysInMonth(new Date(year, month + 1, 0).getDate());
    setFirstDayOfMonth(new Date(year, month, 1).getDay());
  };

  const handlePrevMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
    updateCalendar(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
    updateCalendar(newDate);
  };

  const fetchTodayData = async () => {
    try {
      setLoading(true);
      const [birthdayResponse, leaveResponse] = await Promise.all([
        getThisWeekBirthdays(),
        getTodayLeaves(),
      ]);
      setThisWeekBirthdays(birthdayResponse.data || []);
      setTodayLeaves(leaveResponse.data || []);
    } catch (error) {
      console.error("Error fetching data:", error.message);
      setThisWeekBirthdays([]);
      setTodayLeaves([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch monthly summary
  const fetchMonthlySummary = useCallback(async () => {
    if (!userObj?._id) return;
    try {
      const response = await axios.get(
        `/dashboard/leave-summary/${userObj._id}`
      );
      if (response.data.status === "success") {
        setMonthlySummary(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching monthly summary:", error.message);
    }
  }, [userObj?._id]);

  // Fetch leave details
  const fetchLeaveDetails = useCallback(async () => {
    if (!userObj?._id) return;
    try {
      const response = await axios.get(
        `/dashboard/leave-summary/details/${userObj._id}`
      );
      if (response.data.status === "success") {
        setLeaveDetails(response.data.data.leaves);
      }
    } catch (error) {
      console.error("Error fetching leave details:", error.message);
    }
  }, [userObj?._id]);

  // Initialize calendar on component mount
  useEffect(() => {
    updateCalendar(new Date());

    const handleResize = () => {
      if (window.innerWidth > 768) {
        setRightPanelVisible(true);
      } else {
        setRightPanelVisible(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch user detail once when user ID is available
  useEffect(() => {
    if (!userObj?._id) return;
    const fetchUserData = async () => {
      try {
        const data = await getUserById(userObj._id);
        setUserDetail(data.data);
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };
    fetchUserData();
  }, [userObj?._id]);

  // Fetch today data (birthdays + leaves) ONCE on mount, guarded by ref
  useEffect(() => {
    if (hasFetchedToday.current) return;
    hasFetchedToday.current = true;
    fetchTodayData();
  }, []);

  // Fetch monthly summary, leave details, and start clock on mount
  useEffect(() => {
    if (userObj?._id) {
      fetchMonthlySummary();
      fetchLeaveDetails();
    }
    const refreshCurrentTime = () => {
      const now = new Date();
      const formattedTime = now.toLocaleTimeString();
      setCurrentTime(formattedTime);
    };

    refreshCurrentTime();
    const timer = setInterval(refreshCurrentTime, 1000);
    return () => clearInterval(timer);
  }, [userObj?._id, fetchMonthlySummary, fetchLeaveDetails]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const storedUserData = localStorage.getItem("user");
    if (!storedUserData) {
      showNotification('error', 'Error', "User data not found in local storage");

      return;
    }

    const user = JSON.parse(storedUserData);
    if (!user?.data?._id || !user?.data?.name) {
      showNotification('error', 'Error', "User ID and name are required!");

      return;
    }

    const attendanceData = {
      clockIn: new Date(),
      status: "active",
      user: {
        _id: user.data._id,
        name: user.data.name,
      },
    };

    try {
      const response = await axios.post(
        "/attendance", // Adjust endpoint as needed
        attendanceData
      );
      if (response.data.status === "success") {
        showNotification('success', 'ClockIn data submitted', 'ClockIn data has been submitted successfully!');


        setIsClockInDisabled(true);
        setIsClockOutDisabled(false);
      }
    } catch (error) {
      showNotification('error', 'Error', "Failed to clock in");

    }
  };

  const handleUpdate = async () => {
    if (!todayAttendance || !todayAttendance._id) {
      showNotification('error', 'Error', "No attendance record found for today");

      return;
    }

    try {
      const storedUserData = localStorage.getItem("user");
      const user = JSON.parse(storedUserData);
      const userId = user.data._id;
      const date = new Date().toISOString().split("T")[0];
      const response = await axios.put(
        `/attendance/${userId}/${date}`, // Adjust endpoint as needed
        { clockOut: new Date() }
      );

      if (response.data.status === "success") {

        showNotification('success', 'ClockOut data submitted', 'ClockOut data has been submitted successfully!');


        setIsClockOutDisabled(true);
      }
    } catch (error) {
      showNotification('error', 'error', 'Failed to update ClockOut data. Please try again');


    }
  };

  const toggleRightPanel = () => {
    setRightPanelVisible(!rightPanelVisible);
  };

  const getLeaveChartData = () => {
    if (!monthlySummary.leaveBalances || monthlySummary.leaveBalances.length === 0) {
      return { series: [], labels: [] };
    }

    return {
      series: monthlySummary.leaveBalances.map((balance) => Number(balance.leaveCount) || 0),
      labels: monthlySummary.leaveBalances.map((balance) => balance.leaveType),
      colors: ["#008FFB", "#00E396", "#FEB019", "#FF4560", "#775DD0"],
    };
  };

  const attendanceChartConfigEmp = {
    series: getLeaveChartData().series,
    options: {
      chart: { type: "donut" },
      colors: getLeaveChartData().colors,
      labels: getLeaveChartData().labels,
      dataLabels: { enabled: false },
      legend: { show: false },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: { width: 200 },
            legend: { position: "bottom" },
          },
        },
      ],
    },
  };

  const attendanceWeeklyChart = {
    series: [
      { name: "Last Week", data: [31, 40, 28, 51, 42, 109, 100] },
      { name: "This Week", data: [11, 32, 45, 32, 34, 52, 41] },
    ],
    options: {
      chart: {
        height: 200,
        type: "line",
        toolbar: { show: false },
      },
      colors: ["#FF9800", "#2196F3"],
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 2 },
      xaxis: { categories: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] },
      yaxis: { show: false },
      legend: { show: false },
      grid: { show: false },
    },
  };

  // Get the actual user name safely
  const userName = userDetail?.name || userData?.data?.name || "User";
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) {
      return "Good morning";
    } else if (hour < 17) {
      return "Good afternoon";
    } else {
      return "Good evening";
    }
  };

  return (
    <div className="adm_main_wrapper">  {contextHolder}

      <div className="adm_dash_wrapper">
        <div className="adm_content_container">
          <div className="adm_left_panel">
            <div className="adm_greeting贸易">
              <h1>
                {getGreeting()} {userName}!
              </h1>
              <p>Happening with you today.</p>
              <button
                className="adm_toggle_right_panel"
                onClick={toggleRightPanel}
                aria-label="Toggle right panel"
              >
                <Menu size={24} />
              </button>
            </div>

            <div className="adm_stat_tiles">
              <div className="adm_stat_tile adm_employees_tile">
                <div className="adm_stat_icon">
                  <i className="fa-solid fa-users"></i>
                </div>
                <div className="adm_stat_details">
                  <h2 className="adm_stat_number">
                    <CountUp end={monthlySummary.leaves.totalLeaveDays} duration={3} />
                  </h2>
                  <p className="adm_stat_description">No of Leaves Taken</p>
                </div>
              </div>

              <div className="adm_stat_tile adm_leaves_tile">
                <div className="adm_stat_icon">
                  <i className="fa-solid fa-right-from-bracket"></i>
                </div>
                <div className="adm_stat_details">
                  <h2 className="adm_stat_number">
                    <CountUp
                      end={monthlySummary.leaveBalances.reduce(
                        (sum, balance) => sum + (balance.leaveCount || 0),
                        0
                      )}
                      duration={3}
                    />
                  </h2>
                  <p className="adm_stat_description">Remaining Leaves</p>
                </div>
              </div>

              <div className="adm_stat_tile adm_attendance_tile">
                <div className="adm_stat_icon">
                  <i className="fa-solid fa-calendar-check"></i>
                </div>
                <div className="adm_stat_details">
                  <h2 className="adm_stat_number">
                    <CountUp
                      end={monthlySummary.attendance.totalPresentDays}
                      duration={3}
                    />
                  </h2>
                  <p className="adm_stat_description">Attendance</p>
                </div>
              </div>

              <div className="adm_stat_tile adm_late_tile">
                <div className="adm_stat_icon">
                  <i className="fa-solid fa-clock"></i>
                </div>
                <div className="adm_stat_details">
                  <h2 className="adm_stat_number adm_late_number">
                    <CountUp end={countLate} duration={3} />
                  </h2>
                  <p className="adm_stat_description">Late Attendance</p>
                </div>
              </div>
            </div>

            <div className="adm_dashboard_grid">
              <div className="adm_today_tables_container">
                {/* This Week Birthdays Table */}
                <div className="adm_today_birthday_table_container">
                  <h3 className="adm_table_title">This Week Birthdays</h3>
                  <table className="adm_employee_table">
                    <thead>
                      <tr>
                        <th>Emp Name</th>
                        <th>Birthdate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {thisWeekBirthdays.length > 0 ? (
                        thisWeekBirthdays.map((employee, index) => (
                          <tr key={index}>
                            <td>{employee.name || "-"}</td>
                            <td>
                              {employee.date_of_birth
                                ? dayjs(employee.date_of_birth).format("MMM DD")
                                : "-"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={2} className="adm_empty_cell">
                            No birthdays this week
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Current Month Leave Details Table */}
                <div className="adm_today_leave_table_container">
                  <h3 className="adm_table_title">This Month Leaves</h3>
                  <table className="adm_employee_table">
                    <thead>
                      <tr>
                        <th>Leave Date</th>
                        <th>Leave Type</th>
                        <th>Attendance Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaveDetails.length > 0 ? (
                        leaveDetails.map((leave, index) => (
                          <tr key={index}>
                            <td>{dayjs(leave.date).format("YYYY-MM-DD")}</td>
                            <td>{leave.leaveType}</td>
                            <td>{leave.attendanceType}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="adm_empty_cell">
                            No leaves this month
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="adm_chart_box adm_attendance_donut_box" style={{ display: "none" }}>
                <div className="adm_chart_container">
                  <div className="adm_donut_chart_container">
                    <ReactApexChart
                      options={attendanceChartConfigEmp.options}
                      series={attendanceChartConfigEmp.series}
                      type="donut"
                      height={300}
                    />
                  </div>
                  <div className="adm_employee_table_container">
                    <table className="adm_employee_table">
                      <thead>
                        <tr>
                          <th>Leave Type</th>
                          <th>Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthlySummary.leaveBalances.length > 0 ? (
                          monthlySummary.leaveBalances.map((balance, index) => (
                            <tr key={index}>
                              <td>{balance.leaveType}</td>
                              <td>{balance.leaveCount}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={2} className="adm_empty_cell">
                              No data available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="adm_chart_box adm_attendance_weekly_box">
                <h3 className="adm_chart_title">Attendance Weekly</h3>
                <div className="adm_chart_legend">
                  <div className="adm_legend_item">
                    <span className="adm_legend_dot adm_last_week_dot"></span>
                    <span>Last Week</span>
                  </div>
                  <div className="adm_legend_item">
                    <span className="adm_legend_dot adm_this_week_dot"></span>
                    <span>This Week</span>
                  </div>
                </div>
                <ReactApexChart
                  options={attendanceWeeklyChart.options}
                  series={attendanceWeeklyChart.series}
                  type="line"
                  height={200}
                />
              </div>
            </div>
          </div>

          <div
            className={`adm_right_panel ${rightPanelVisible ? "adm_right_panel_visible" : ""
              }`}
          >
            <div className="adm_right_panel_close">
              <button
                className="adm_close_right_panel"
                onClick={toggleRightPanel}
              >
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            <div className="adm_profile_container">
              <div className="adm_user_profile_box">
                {userDetail?.profilePic ? (
                  <img
                    src={`${base_path_img}${userDetail.profilePic}`}
                    alt="User profile"
                    className="adm_profile_avatar"
                  />
                ) : (
                  <img
                    src={profileImageUrl}
                    alt="User_profile"
                    className="adm_profile_avatar"
                  />
                )}
                <div className="adm_user_info_box">
                  <h3 className="adm_profile_h3">{userName}</h3>
                  <p className="adm_profile_p">{currentTime}</p>
                </div>
              </div>
            </div>

            <div className="adm_sidebar_content">
              <div className="adm_calendar_container">
                <div className="adm_calendar_header">
                  <h3>{selectedMonth}</h3>
                  <div className="adm_calendar_navigation">
                    <button
                      className="adm_prev_month_btn"
                      onClick={() => handlePrevMonth(currentDate)}
                    >
                      <i className="fa-solid fa-chevron-left"></i>
                    </button>
                    <button
                      className="adm_next_month_btn"
                      onClick={() => handleNextMonth(currentDate)}
                    >
                      <i className="fa-solid fa-chevron-right"></i>
                    </button>
                  </div>
                </div>

                <div className="adm_calendar_weekdays">
                  {weekdays.map((day) => (
                    <div key={day} className="adm_weekday_label">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="adm_calendar_days_grid">
                  {Array.from({ length: firstDayOfMonth }).map((_, index) => (
                    <div
                      key={`empty-${index}`}
                      className="adm_calendar_empty_day"
                    ></div>
                  ))}

                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(
                    (day) => {
                      const isToday =
                        day === new Date().getDate() &&
                        currentDate.getMonth() === new Date().getMonth() &&
                        currentDate.getFullYear() === new Date().getFullYear();

                      return (
                        <div
                          key={day}
                          className={`adm_calendar_day ${isToday ? "adm_is_today" : ""
                            }`}
                        >
                          {day}
                        </div>
                      );
                    }
                  )}
                </div>
              </div>

              <div className="action-buttons" style={{ display: "none" }}>
                <button
                  className="clock-in"
                  onClick={handleSubmit}
                  disabled={isClockInDisabled}
                >
                  Clock In
                </button>
                <button
                  className="clock-out"
                  onClick={handleUpdate}
                  disabled={isClockOutDisabled}
                >
                  Clock Out
                </button>
              </div>

              <div className="adm_reminders_container">
                <div className="adm_reminders_header">
                  <h3>Reminders</h3>
                  <button className="adm_add_reminder_btn">
                    <i className="fa-solid fa-plus"></i>
                  </button>
                </div>
                <div className="adm_todays_reminders">
                  <h4>Today</h4>
                  <div className="adm_reminder_item">
                    <div className="adm_reminder_icon adm_pay_date_icon">
                      <i className="fa-solid fa-calendar"></i>
                    </div>
                    <div className="adm_reminder_content">
                      <p>Next pay date</p>
                      <p className="adm_reminder_date">
                        {dayjs().endOf("month").format("YYYY-MM-DD")}
                      </p>
                    </div>
                  </div>
                  <div className="adm_reminder_item">
                    <div className="adm_reminder_icon adm_meeting_icon">
                      <i className="fa-solid fa-users"></i>
                    </div>
                    <div className="adm_reminder_content">
                      <p>Team meeting</p>
                      <p className="adm_reminder_time">4:00pm</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDashboard;